import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Linkedin, ArrowRight } from 'lucide-react';

const DANIEL_LINKEDIN = 'https://www.linkedin.com/in/daniel-dominguez-g/';

export default function TeamSection() {
  const { t } = useLanguage();
  const ref = useScrollReveal();

  const specialties = t('team.daniel_specialties').split(', ');

  return (
    <section id="team" data-testid="team-section" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 bg-[#0B1E2E]" />

      <div ref={ref} className="reveal relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <p className="text-sm uppercase tracking-widest text-[#42D2D9] font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('team.caption')}
        </p>
        <div className="section-line mb-8" />

        <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white mb-16 max-w-3xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('team.title')}
        </h2>

        {/* Daniel profile card */}
        <div className="glass-card rounded-lg overflow-hidden" data-testid="team-daniel-card">
          <div className="grid lg:grid-cols-5 gap-0">
            {/* Left: Visual + Name */}
            <div className="lg:col-span-2 p-8 md:p-12 flex flex-col justify-center items-center lg:items-start bg-gradient-to-br from-[#1A4D75]/30 to-transparent">
              {/* Photo */}
              <div className="w-36 h-36 md:w-44 md:h-44 rounded-full overflow-hidden mb-6 border-2 border-[#42D2D9]/20">
                <img
                  src="https://customer-assets.emergentagent.com/job_plastic-wealth/artifacts/ksn2pryk_image.png"
                  alt="Daniel Domínguez"
                  className="w-full h-full object-cover"
                />
              </div>

              <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 text-center lg:text-left" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('team.daniel_name')}
              </h3>
              <p className="text-[#42D2D9] font-semibold text-sm uppercase tracking-widest mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('team.daniel_role')}
              </p>

              {/* LinkedIn button */}
              <a
                href={DANIEL_LINKEDIN}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="team-daniel-linkedin"
                className="inline-flex items-center gap-2 px-5 py-3 rounded border border-[#0A66C2]/40 bg-[#0A66C2]/10 text-[#5BA3D9] hover:bg-[#0A66C2]/20 hover:text-white transition-all text-sm font-medium"
              >
                <Linkedin className="w-4 h-4" />
                {t('team.linkedin')}
              </a>
            </div>

            {/* Right: Bio + Specialties */}
            <div className="lg:col-span-3 p-8 md:p-12 flex flex-col justify-center">
              <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-8">
                {t('team.daniel_bio')}
              </p>

              {/* Specialties tags */}
              <div className="flex flex-wrap gap-3 mb-8">
                {specialties.map((s, i) => (
                  <span
                    key={i}
                    className="px-4 py-2 rounded-full border border-[#42D2D9]/20 bg-[#42D2D9]/5 text-xs text-[#42D2D9] font-medium uppercase tracking-wider"
                  >
                    {s}
                  </span>
                ))}
              </div>

              {/* CTA */}
              <a
                href="#booking"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth' });
                }}
                data-testid="team-cta-booking"
                className="inline-flex items-center gap-2 text-sm text-white underline decoration-[#42D2D9] decoration-2 underline-offset-8 hover:text-[#42D2D9] transition-colors w-fit"
              >
                {t('team.cta')}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
